import React, { Dispatch, SetStateAction } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { Settings } from '../../models/settings.model';
import { legendValid } from '../../tools/legend.data.entry.tool';
import { modalHide, modalShow } from '../../tools/modal.tool';
import { measureUnitSelected } from '../../scripts/list.selected.item.script';
import { unitProductList, unitServiceList } from '../../scripts/list.values.script';
import { prefixLegend, suffixLegend } from '../../scripts/name.legend.script';

import AppLegend from './Legend';
import AppModalSettingCreateUnit from '../modal/ModalSettingsCreateUnit';

export interface AppInputMeasureUnitProps {
  component?: string,
  options: {type: string, main_modal: string | null, index: number | string | null, sub_index: number | string | null},
  companyForUser: Company,
  settingsForCompany: Settings,
  measureUnit?: {value: string, valid: boolean},
  variant?: {value: any, valid: any},
  setMeasureUnit?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setVariant?: Dispatch<SetStateAction<{value: any, valid: any}>>,
  loadSettingsForCompany: Function
};

const AppInputMeasureUnit: React.FunctionComponent<AppInputMeasureUnitProps> = ({component, companyForUser, settingsForCompany, options, measureUnit, variant, setMeasureUnit, setVariant, loadSettingsForCompany}) => {
  const {lang} = StateLanguage()

  const handleChangeUnit = (item: string) => {
    if (measureUnit && setMeasureUnit) {
      setMeasureUnit({value: item, valid: true})
      legendValid(`${prefixLegend(component)}legend-unit-required${suffixLegend(options.index, options.sub_index)}`)
    }
  }

  const handleChangeUnitToVariant = (item: string) => {
    if (variant && setVariant) {
      setVariant({value: {...variant.value, unit: item}, valid: {...variant.valid, unit: true}})
      legendValid(`${prefixLegend(component)}legend-variant-unit-required${suffixLegend(options.index, options.sub_index)}`)
    }
  }

  const executeShowModalSettingsCreateMeasureUnit = () => {
    if (options.main_modal) {
      modalHide(options.main_modal)
    }

    modalShow(`modal-settings-create-measure-unit${suffixLegend(options.index, options.sub_index)}`)
  }

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-100 p-0">
          <span className="selection">
            <span className="select2-selection select2-selection--single form-select form-select-solid bg-light">
              <span className="select2-selection__rendered" role="textbox">
                { measureUnit &&
                  <>
                    {measureUnit.value.length > 0 ? measureUnitSelected(lang, settingsForCompany.measure_units, options.type, measureUnit.value) : lang.labels.selectOption}
                  </>
                }
                { variant && 
                  <>
                    {variant.value.unit.length > 0 ? measureUnitSelected(lang, settingsForCompany.measure_units, options.type, variant.value.unit) : lang.labels.selectOption}
                  </>
                }
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox">
                <li className="select2-results__option select2-results__option--selectable">
                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={executeShowModalSettingsCreateMeasureUnit}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-3 me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                          <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                          <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.addUnit}
                    </div>
                  </Dropdown.Item>
                </li>
                <li className={`select2-results__option select2-results__option--selectable ${unitServiceList(settingsForCompany.measure_units).length > 0 && "d-none"}`}>
                  <Dropdown.Item bsPrefix="select2-results__option__text">{lang.labels.selectOption}</Dropdown.Item>
                </li>
                { options && options.type === "service" &&
                  <>
                    { measureUnit && unitServiceList(settingsForCompany.measure_units).map (( (other_item, other_index) => { return (
                      <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_item.id === measureUnit.value && "select2-results__option--selected"}`} role="option" aria-selected={other_item.id === measureUnit.value}>
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeUnit(other_item.id)}>{other_item.name}</Dropdown.Item>
                      </li>
                    )}))}
                    { variant && unitServiceList(settingsForCompany.measure_units).map (( (other_item, other_index) => { return (
                      <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_item.id === variant.value.unit && "select2-results__option--selected"}`} role="option" aria-selected={other_item.id === variant.value.unit}>
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeUnitToVariant(other_item.id)}>{other_item.name}</Dropdown.Item>
                      </li>
                    )}))}
                  </>
                }
                { options && options.type === "product" &&
                  <>
                    { measureUnit && unitProductList(settingsForCompany.measure_units).map (( (other_item, other_index) => { return (
                      <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_item.id === measureUnit.value && "select2-results__option--selected"}`} role="option" aria-selected={other_item.id === measureUnit.value}>
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeUnit(other_item.id)}>{other_item.name}</Dropdown.Item>
                      </li>
                    )}))}
                    { variant && unitProductList(settingsForCompany.measure_units).map (( (other_item, other_index) => { return (
                      <li key={other_index} className={`select2-results__option select2-results__option--selectable ${other_item.id === variant.value.unit && "select2-results__option--selected"}`} role="option" aria-selected={other_item.id === variant.value.unit}>
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeUnitToVariant(other_item.id)}>{other_item.name}</Dropdown.Item>
                      </li>
                    )}))}
                  </>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      { measureUnit &&
        <>
          <AppLegend attribute={{check: measureUnit.valid, name: "unit", index: options.index, sub_index: options.sub_index}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
        </>
      }
      { variant && 
        <>
          <AppLegend component={component} attribute={{check: variant.valid.unit, name: "variant-unit", index: options.index, sub_index: options.sub_index}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
        </>
      }
      { options && !options.main_modal &&
        <AppModalSettingCreateUnit companyForUser={companyForUser} options={options} variant={variant} setUnitMeasure={setMeasureUnit} setVariant={setVariant} loadSettingsForCompany={loadSettingsForCompany}></AppModalSettingCreateUnit>
      }
    </>
  )
}

export default AppInputMeasureUnit;
